import { ShareAndroid } from 'app/design/icons/shareAndroid';
import { Box, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Itinerary } from 'app/design/icons/itinerary';
import { PinPoint } from 'app/design/icons/pinpoint';
import { useTranslation } from 'react-i18next';
import { ScrollView } from 'react-native-gesture-handler';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { IconInfoCircle } from '@tabler/icons-react-native';
import { theme } from './shared';

export function PlaceActions({
  horizontalPadding,
  onFrequentingPress,
  onHelp,
  onItinerary,
  onShare,
  variant,
}: {
  horizontalPadding: number;
  onFrequentingPress: () => void;
  onHelp: () => void;
  onItinerary: () => void;
  onShare: () => void;
  variant: {
    membership: 'frequenting' | 'not-frequenting';
    membershipAction: 'idle' | 'pending';
    viewer: 'self' | 'visitor';
    website: 'available' | 'unavailable';
  };
}) {
  const { t } = useTranslation();
  const isFrequenting = variant.membership === 'frequenting';
  if (variant.viewer === 'self') return null;
  return (
    <Box style={{ marginHorizontal: -horizontalPadding }}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={{
          gap: theme.spacing['spacing.general.x2'],
          paddingHorizontal: horizontalPadding,
          paddingVertical: theme.spacing['spacing.general.x3'],
        }}
      >
        <Button
          flex={1}
          justifyContent="center"
          loading={variant.membershipAction === 'pending'}
          onPress={onFrequentingPress}
          style={{
            backgroundColor: isFrequenting ? theme.colors['surface.tertiary'] : theme.colors['surface.inverse.default'],
            borderRadius: theme.borderRadii['radius.xl'],
            borderWidth: isFrequenting ? 2 : 0,
            borderColor: isFrequenting ? theme.colors['surface.brand.default'] : undefined,
            gap: theme.spacing['spacing.general.x0,5'],
            alignItems: 'center',
          }}
          variant="primary"
        >
          <PinPoint
            color={isFrequenting ? theme.colors['surface.brand.default'] : theme.colors['surface.default']}
            width={18}
            height={18}
          />
          <Text
            color={isFrequenting ? 'text.brand' : 'text.inverse.primary'}
            fontWeight="700"
            textAlign="center"
            fontSize={12}
          >
            {isFrequenting
              ? t('place.frequenting', { ns: 'neighbour-profile' })
              : t('place.joinButton', { ns: 'neighbour-profile' })}
          </Text>
        </Button>
        <Button
          justifyContent="center"
          onPress={onItinerary}
          style={{
            borderRadius: theme.borderRadii['radius.xl'],
            borderWidth: 1.5,
            borderColor: theme.colors['surface.disabled'],
            paddingHorizontal: theme.spacing['spacing.general.x4'],
          }}
        >
          <Row alignItems="center" gap="spacing.general.x0,5">
            <Itinerary color={theme.colors['text.primary']} width={18} height={18} />
            <Text fontSize={11} fontWeight="600">
              {t('place.itinerary', { ns: 'neighbour-profile' })}
            </Text>
          </Row>
        </Button>
        {variant.website === 'available' ? (
          <Button
            justifyContent="center"
            onPress={onShare}
            style={{
              borderRadius: theme.borderRadii['radius.xl'],
              borderWidth: 1.5,
              borderColor: theme.colors['surface.disabled'],
              paddingHorizontal: theme.spacing['spacing.general.x4'],
            }}
          >
            <Row alignItems="center" gap="spacing.general.x2">
              <ShareAndroid color={theme.colors['text.primary']} width={18} height={18} />
              <Text fontSize={11} fontWeight="600">
                {t('place.share', { ns: 'neighbour-profile' })}
              </Text>
            </Row>
          </Button>
        ) : null}
        <Button
          justifyContent="center"
          onPress={onHelp}
          style={{
            borderRadius: theme.borderRadii['radius.xl'],
            borderWidth: 1.5,
            borderColor: theme.colors['surface.disabled'],
            paddingHorizontal: theme.spacing['spacing.general.x4'],
          }}
        >
          <Row alignItems="center" gap="spacing.general.x0,5">
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.primary']} />
            <Text fontSize={11} fontWeight="600">
              {t('placeHelp.help', { ns: 'neighbour-profile' })}
            </Text>
          </Row>
        </Button>
      </ScrollView>
    </Box>
  );
}
